import { Component, computed, input, output } from '@angular/core'
import { MonthStringPickerComponent } from './month-string-picker.component'
import { DateStringPickerComponent } from './date-string-picker.component'
import { TimeStringPickerComponent } from './time-string-picker.component'
import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'

@Component({
  selector: 'app-date-range-date',
  imports: [
    MonthStringPickerComponent,
    DateStringPickerComponent,
    TimeStringPickerComponent,
  ],
  template: `
    @if (onlyMonth()) {
      <div
          monthStringPicker
          [value]="dateString()"
          (valueChange)="handleDateChange($event)"
          (enter)="enter.emit()"
      >
      </div>
    } @else {
      <div
          dateStringPicker
          [value]="dateString()"
          (valueChange)="handleDateChange($event)"
          (enter)="enter.emit()"
      >
      </div>

      @if (showTime()) {
        <div
            class="mt-2"
            timeStringPicker
            [disabled]="!value()"
            [value]="timeString()"
            [showSeconds]="showSeconds()"
            (valueChange)="handleTimeChange($event)"
        >
        </div>
      }
    }
  `,
})
export class DateRangeDateComponent {
  readonly value = input.required<Date | null>()
  readonly showTime = input.required<boolean>()
  readonly showSeconds = input.required<boolean>()
  readonly onlyMonth = input.required<boolean>()
  readonly endOfDay = input(false)
  readonly tz = input('EST')

  readonly enter = output()
  readonly valueChange = output<TZDate>()

  private asTzDate(): TZDate | null {
    const value = this.value()
    return value ? new TZDate(value.getTime(), this.tz()) : null
  }

  private formatted(pattern: string): string | null {
    const tzDate = this.asTzDate()
    return tzDate ? format(tzDate, pattern) : null
  }

  protected readonly dateString = computed((): string => this.formatted('yyyy-MM-dd') ?? '')

  protected readonly timeString = computed((): string | null => this.formatted('HH:mm:ss'))

  protected readonly fullIsoString = computed((): string => this.asTzDate()?.toISOString() ?? '')

  private emit(dateString: string) {
    this.valueChange.emit(new TZDate(dateString, this.tz()))
  }

  protected handleDateChange(isoDate: string) {
    const getTimePart = () => {
      if (this.showTime()) {
        const fullIsoString = this.fullIsoString()
        if (fullIsoString) {
          // e.g. fullIsoString 2026-02-09T10:26:32.000-05:00
          // becomes yyyy-MM-dd + 'T10:26:32.000-05:00'
          return fullIsoString.slice(10)
        }
      }
      // default time fields based on end of day flag
      // e.g. T00:00:00.000-05:00 or T23:59:59.999-05:00
      const time = this.endOfDay() ? '23:59:59.999' : '00:00:00.000'
      return `T${time}${new TZDate(Date.now(), this.tz()).toISOString().slice(23)}`
    }
    this.emit(`${isoDate}${getTimePart()}`)
  }

  protected handleTimeChange(timeString: string) {
    const fullIsoString = this.fullIsoString()
    if (fullIsoString) {
      // e.g. fullIsoString 2026-02-09T10:26:32.211-05:00
      // becomes '2026-02-09T' + HH:mm:ss + '.000-05:00'
      this.emit(`${fullIsoString.slice(0, 11)}${timeString}.000${fullIsoString.slice(23)}`)
    }
  }
}